import { OnboardingTarget, onboardingTarget } from "../onboarding/onboardingTargets";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ErrorIcon from "@mui/icons-material/Error";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, ButtonGroup } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";

import {
  CollapsableColumn,
  SelectedTab,
  setIsCollapsableColumnOpen,
  setSelectedTab,
} from "../../app/slices/GlobalSlice";
import {
  selectIsPlaying,
  selectTakes,
  selectTrainingType,
  setIsPlaying,
  TrainingType,
} from "../../app/slices/TrainingSlice";
import { selectErrors } from "../../app/slices/ValidationSlice";
import { useTestProject } from "../../hooks/TestProjectHook";
import { hasBlockingValidationErrors } from "./trainingActionbarValidation";

interface TestMode {
  text: string;
  onClick: () => void;
}

export default function TestTrainingSplitButton() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isPlaying = useSelector(selectIsPlaying);
  const takes = useSelector(selectTakes);
  const trainingType = useSelector(selectTrainingType);
  const allErrors = useSelector(selectErrors);
  const takesCount = takes.length;

  const hasErrors = useMemo(
    () => hasBlockingValidationErrors(takes, allErrors),
    [allErrors, takes]
  );

  const { getCurrentProjectTestData, testCurrentBubble, testFromCurrentTake } =
    useTestProject();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const modes: TestMode[] = [
    { text: t("current_project"), onClick: getCurrentProjectTestData },
    {
      text: t(
        trainingType === TrainingType.Training360
          ? "current_bubble"
          : "current_take"
      ),
      onClick: testCurrentBubble,
    },
    {
      text: t(
        trainingType === TrainingType.Training360
          ? "from_current_bubble"
          : "from_current_take"
      ),
      onClick: testFromCurrentTake,
    },
  ];

  const stopPlaying = () => {
    dispatch(setIsPlaying({ isPlaying: false, data: "" }));
    dispatch(setSelectedTab(SelectedTab.Timeline));
  };

  const openErrorLog = () => {
    dispatch(
      setIsCollapsableColumnOpen({
        open: true,
        column: CollapsableColumn.ErrorLog,
      })
    );
  };

  const isErrorState = hasErrors;
  const isStopState = isPlaying && !isErrorState;
  const isReadyState = !isErrorState && !isStopState;
  const variant = isReadyState ? "piBlueOutlined" : "piRedOutlined";

  const mainIcon = isErrorState ? (
    <ErrorIcon sx={{ fontSize: 16 }} />
  ) : isStopState ? (
    <StopIcon sx={{ fontSize: 16 }} />
  ) : (
    <PlayArrowIcon sx={{ fontSize: 16 }} />
  );

  const mainLabel = isErrorState
    ? "Resolve Errors"
    : isStopState
    ? "Stop"
    : modes[selectedIndex].text;

  const handleMainClick = () => {
    if (isErrorState) {
      openErrorLog();
      return;
    }
    if (isStopState) {
      stopPlaying();
      return;
    }
    modes[selectedIndex].onClick();
  };

  const handleToggleDropdown = () => setOpen((prev) => !prev);

  const handleSelectMode = (index: number) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleClickAway = () => setOpen(false);

  const mainDisabled = isReadyState && takesCount === 0;
  const dropdownDisabled = isStopState || isErrorState || mainDisabled;

  const MAIN_WIDTH = 180;
  const variantColor =
    variant === "piRedOutlined" ? "var(--pi-error)" : "var(--pi-key-blue)";

  const pillSx = {
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: "13px",
    height: 32,
    minWidth: 0,
    border: "1.5px solid",
    background: "transparent",
    transition:
      "background 200ms ease, color 200ms ease, border-color 200ms ease",
    "&:hover": {
      background: `color-mix(in srgb, ${variantColor} 10%, transparent)`,
      color: variantColor,
      borderColor: variantColor,
    },
    "&:first-of-type": {
      borderTopLeftRadius: "5px",
      borderBottomLeftRadius: "5px",
      paddingLeft: "12px",
      paddingRight: "12px",
      gap: "6px",
      width: MAIN_WIDTH,
      minWidth: MAIN_WIDTH,
      maxWidth: MAIN_WIDTH,
    },
    "&:last-of-type": {
      borderTopRightRadius: "5px",
      borderBottomRightRadius: "5px",
      paddingLeft: "4px",
      paddingRight: "6px",
      minWidth: 28,
    },
    "&.Mui-disabled": {
      borderColor: variantColor,
      color: variantColor,
      opacity: 0.55,
      background: "transparent",
    },
  };

  const labelSx = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    minWidth: 0,
    flex: 1,
    textAlign: "center" as const,
  };

  return (
    <div>
      <ButtonGroup
        ref={anchorRef}
        aria-label="test training split button"
        disableElevation
        {...onboardingTarget(OnboardingTarget.ActionbarTestButton)}
      >
        <Button
          aria-label="pisplitbutton-main-action"
          variant={variant}
          onClick={handleMainClick}
          disabled={mainDisabled}
          sx={pillSx}
        >
          {mainIcon}
          <Box component="span" sx={labelSx} title={mainLabel}>
            {mainLabel}
          </Box>
        </Button>
        <Button
          aria-label="pisplitbutton-dropdown-button"
          variant={variant}
          onClick={handleToggleDropdown}
          disabled={dropdownDisabled}
          aria-haspopup="menu"
          aria-expanded={open ? "true" : undefined}
          sx={pillSx}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
        </Button>
      </ButtonGroup>

      <Popper
        sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-end" ? "right top" : "right bottom",
              transform: "translate(0, 8px)",
              borderRadius: "5px",
            }}
          >
            <Paper
              sx={{
                borderRadius: "5px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              }}
            >
              <ClickAwayListener onClickAway={handleClickAway}>
                <MenuList
                  className="pipopper-menu"
                  autoFocusItem
                  style={{
                    maxHeight: "250px",
                    overflowY: "auto",
                    border: "none",
                  }}
                >
                  {modes.map((mode, index) => (
                    <MenuItem
                      key={mode.text}
                      aria-label={mode.text}
                      selected={index === selectedIndex}
                      onClick={() => handleSelectMode(index)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "8px",
                        fontFamily: "Poppins",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "var(--pi-text-color-1)",
                        padding: "8px 10px",
                        "&:hover": {
                          backgroundColor: "var(--pi-main-hover)",
                        },
                        "&.Mui-selected": {
                          color: "var(--pi-key-blue)",
                          fontWeight: 600,
                          backgroundColor:
                            "color-mix(in srgb, var(--pi-key-blue) 8%, transparent) !important",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor:
                            "color-mix(in srgb, var(--pi-key-blue) 14%, transparent) !important",
                        },
                      }}
                    >
                      <span>{mode.text}</span>
                      {index === selectedIndex && (
                        <CheckIcon sx={{ fontSize: 16 }} />
                      )}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
}
